import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { adminApi, type FlagWithContext } from "@/lib/admin-api";
import { FlagDetail } from "./-components/FlagDetail";

export const Route = createFileRoute("/admin/flags")({
  component: AdminFlagsPage,
});

interface Toast {
  id: number;
  message: string;
}

export function AdminFlagsPage() {
  const [flags, setFlags] = useState<FlagWithContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlag, setSelectedFlag] = useState<FlagWithContext | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastCounterRef = useRef(0);

  const addToast = useCallback((message: string) => {
    const id = ++toastCounterRef.current;
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const loadFlags = useCallback(async () => {
    try {
      const { flags: data } = await adminApi.listFlags();
      setFlags(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  function handleResolved(id: number) {
    setFlags((prev) => prev.filter((f) => f.id !== id));
    setSelectedFlag(null);
  }

  function handleAlreadyResolved() {
    addToast("Flag already resolved — refreshing.");
    setSelectedFlag(null);
    setLoading(true);
    void loadFlags();
  }

  const formatDate = (iso: string) => iso.slice(0, 10);
  const truncate = (s: string, n: number) => (s.length > n ? s.slice(0, n) + "…" : s);

  return (
    <div className="admin-page" data-testid="admin-flags">
      <div className="flags-header">
        <span className="flags-header__eyebrow">FLAGS</span>
        <h1 className="flags-header__title">Pending review</h1>
      </div>

      {loading ? (
        <div className="flags-loading" data-testid="flags-loading">
          LOADING…
        </div>
      ) : flags.length === 0 ? (
        <div className="flags-empty" data-testid="flags-empty">
          <span className="flags-empty__icon">▮</span>
          <p className="flags-empty__text">No flags awaiting review.</p>
        </div>
      ) : (
        <table className="flags-table" aria-label="Pending flags">
          <thead>
            <tr>
              <th className="flags-table__th">Date</th>
              <th className="flags-table__th">Artifact</th>
              <th className="flags-table__th">Automaton</th>
              <th className="flags-table__th">Reporter</th>
              <th className="flags-table__th">Reason</th>
            </tr>
          </thead>
          <tbody>
            {flags.map((flag) => (
              <tr
                key={flag.id}
                className="flags-table__row"
                onClick={() => setSelectedFlag(flag)}
                tabIndex={0}
                role="button"
                aria-label={`Review flag for ${flag.artifact_title}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedFlag(flag);
                }}
                data-testid={`flag-row-${flag.id}`}
              >
                <td className="flags-table__td flags-table__td--date">
                  {formatDate(flag.created_at)}
                </td>
                <td className="flags-table__td flags-table__td--title">
                  {truncate(flag.artifact_title, 48)}
                </td>
                <td className="flags-table__td">{flag.automaton_handle}</td>
                <td className="flags-table__td flags-table__td--reporter">
                  {flag.reporter_email ?? "auto:safety_filter"}
                </td>
                <td className="flags-table__td">{flag.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedFlag && (
        <FlagDetail
          flag={selectedFlag}
          onClose={() => setSelectedFlag(null)}
          onResolved={handleResolved}
          onAlreadyResolved={handleAlreadyResolved}
        />
      )}

      {toasts.length > 0 && (
        <div className="toast-stack" aria-live="assertive" role="status">
          {toasts.map((t) => (
            <div key={t.id} className="toast" data-testid="toast">
              {t.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
